/**
 * L'Chaim Vault — every reflection, image, and form the scholar has logged.
 * Distressed blueprint aesthetic matching LChaimDashboard.
 */
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import {
  ArrowLeft, Archive, Loader2, BookOpen, Calendar, FileText, Image as ImageIcon, Video,
} from 'lucide-react';
import api from '../services/api';
import { toast } from 'sonner';

const COLORS = {
  blueprintBg: '#0c2243',
  blueprintGrid: 'rgba(255,255,255,0.08)',
  cyan: '#00d4ff',
  red: '#dc2626',
  parchment: '#f1ead7',
  ink: '#1a2333',
};

const blueprintBg = {
  backgroundColor: COLORS.blueprintBg,
  backgroundImage: `
    linear-gradient(${COLORS.blueprintGrid} 1px, transparent 1px),
    linear-gradient(90deg, ${COLORS.blueprintGrid} 1px, transparent 1px),
    radial-gradient(circle at 30% 20%, rgba(255,255,255,0.05), transparent 60%)
  `,
  backgroundSize: '32px 32px, 32px 32px, 100% 100%',
};

const parchmentBg = {
  backgroundColor: COLORS.parchment,
  backgroundImage: `
    radial-gradient(circle at 20% 30%, rgba(0,0,0,0.06), transparent 50%),
    repeating-linear-gradient(45deg, transparent, transparent 12px, rgba(0,0,0,0.015) 12px, rgba(0,0,0,0.015) 13px)
  `,
  color: COLORS.ink,
};

const fileIconFor = (type) => {
  if (!type) return <FileText className="w-4 h-4" />;
  if (type.startsWith('image')) return <ImageIcon className="w-4 h-4" />;
  if (type.startsWith('video')) return <Video className="w-4 h-4" />;
  return <FileText className="w-4 h-4" />;
};

const LChaimVault = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [journalEntries, setJournalEntries] = useState([]);
  const [files, setFiles] = useState([]);

  useEffect(() => {
    (async () => {
      const token = localStorage.getItem('academy_token');
      if (!token) {
        navigate('/academy/lchaim/login');
        return;
      }
      try {
        // Both endpoints exist in server.py
        const [vaultRes, journalRes] = await Promise.all([
          api.get('/academy/lchaim/vault', { headers: { Authorization: `Bearer ${token}` } }),
          api.get('/academy/lchaim/journal', { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: { entries: [] } })),
        ]);
        setFiles(vaultRes.data?.files || []);
        setJournalEntries(journalRes.data?.entries || journalRes.data?.journal || []);
      } catch (e) {
        toast.error('Failed to load vault');
      } finally {
        setLoading(false);
      }
    })();
  }, [navigate]);

  return (
    <div className="min-h-screen text-white relative" style={blueprintBg} data-testid="lchaim-vault-page">
      <div className="absolute inset-3 sm:inset-5 pointer-events-none border-2 border-white/35"
           style={{ boxShadow: 'inset 0 0 80px rgba(0,0,0,0.4)' }} />

      <div className="relative max-w-3xl mx-auto px-5 sm:px-10 py-6 pb-20">
        <div className="flex items-center justify-between mb-6">
          <Button variant="ghost" size="sm" className="text-white/70 hover:text-white -ml-2"
                  onClick={() => navigate('/academy/lchaim/dashboard')} data-testid="vault-back-btn">
            <ArrowLeft className="w-4 h-4 mr-1" /> Dashboard
          </Button>
          <div className="flex items-center gap-2">
            <Archive className="w-5 h-5" style={{ color: COLORS.cyan }} />
            <span className="text-xs tracking-[0.4em] uppercase font-bold" style={{ color: COLORS.cyan }}>
              The Vault
            </span>
          </div>
        </div>

        <div className="text-center mb-8">
          <h1 className="text-3xl sm:text-4xl font-black uppercase tracking-tight">
            <span className="text-white">YOUR </span>
            <span style={{ color: COLORS.cyan }}>SACRED RECORD</span>
          </h1>
          <p className="mt-2 text-xs tracking-[0.3em] uppercase text-white/55">
            Every reflection · every charge · every receipt of the work
          </p>
        </div>

        {loading ? (
          <div className="py-20 flex justify-center">
            <Loader2 className="w-8 h-8 animate-spin" style={{ color: COLORS.cyan }} />
          </div>
        ) : (
          <>
            {/* Stats strip */}
            <div className="grid grid-cols-2 gap-2 mb-8">
              <div className="border border-white/15 px-3 py-3 bg-white/[0.03]">
                <div className="text-[9px] tracking-[0.3em] uppercase text-white/50">Reflections</div>
                <div className="text-2xl font-black font-mono mt-1">{journalEntries.length}</div>
              </div>
              <div className="border border-white/15 px-3 py-3 bg-white/[0.03]">
                <div className="text-[9px] tracking-[0.3em] uppercase text-white/50">Files</div>
                <div className="text-2xl font-black font-mono mt-1">{files.length}</div>
              </div>
            </div>

            {/* Reflections */}
            <section className="mb-10">
              <div className="flex items-center gap-2 mb-4">
                <BookOpen className="w-4 h-4" style={{ color: '#fff' }} />
                <h2 className="text-xs tracking-[0.3em] uppercase font-black">Reflections</h2>
              </div>
              {journalEntries.length === 0 ? (
                <div className="border border-white/15 p-6 text-center text-xs text-white/55">
                  No reflections yet. Save one from your daily dashboard.
                </div>
              ) : (
                <div className="space-y-3">
                  {journalEntries.map((j, i) => (
                    <div key={i} className="px-5 py-4" style={parchmentBg} data-testid={`vault-entry-${i}`}>
                      <div className="flex items-center justify-between mb-2">
                        <div className="text-[9px] tracking-[0.35em] uppercase font-black" style={{ color: COLORS.ink }}>
                          Day {j.day} · Chord {j.chord}
                        </div>
                        {j.created_at && (
                          <div className="text-[9px] tracking-[0.2em] uppercase opacity-60 flex items-center gap-1">
                            <Calendar className="w-3 h-3" />
                            {new Date(j.created_at).toLocaleDateString()}
                          </div>
                        )}
                      </div>
                      <p className="text-sm leading-relaxed whitespace-pre-wrap">{j.entry}</p>
                    </div>
                  ))}
                </div>
              )}
            </section>

            {/* Files */}
            <section>
              <div className="flex items-center gap-2 mb-4">
                <FileText className="w-4 h-4" style={{ color: '#fff' }} />
                <h2 className="text-xs tracking-[0.3em] uppercase font-black">Files & Uploads</h2>
              </div>
              {files.length === 0 ? (
                <div className="border border-white/15 p-6 text-center text-xs text-white/55">
                  No uploads yet. Files attached to your daily charges will appear here.
                </div>
              ) : (
                <div className="grid sm:grid-cols-2 gap-3">
                  {files.map((f, i) => (
                    <a key={i} href={f.url || '#'} target="_blank" rel="noreferrer"
                       className="block border border-white/20 bg-white/[0.03] hover:bg-white/[0.08] p-3 transition-colors"
                       data-testid={`vault-file-${i}`}>
                      <div className="flex items-center gap-2 mb-2">
                        {fileIconFor(f.type)}
                        <span className="text-[10px] tracking-[0.25em] uppercase font-bold text-white/70">
                          {(f.type || 'file').split('/')[0]}
                        </span>
                      </div>
                      <div className="text-xs font-bold text-white truncate">{f.name || 'Untitled'}</div>
                      {f.created_at && (
                        <div className="text-[10px] text-white/40 mt-1">
                          {new Date(f.created_at).toLocaleDateString()}
                        </div>
                      )}
                    </a>
                  ))}
                </div>
              )}
            </section>
          </>
        )}
      </div>
    </div>
  );
};

export default LChaimVault;
